// Google Analytics 4 Integration for Skanteq
import ReactGA from "react-ga4";

export interface SkanteqEventData {
  owner_email?: string;
  owner_business_name?: string;
  marker_id?: string;
  experience_id?: string;
  clicked_url?: string;
  product_name?: string;
  product_price?: number;
  product_id?: string;
  order_total?: number;
  user_email?: string;
  business_name?: string;
  registration_method?: string;
  [key: string]: any;
}

export interface User {
  protected?: {
    email?: string;
  };
  uid?: string;
}

export interface BusinessAccount {
  business_name?: string;
  name?: string;
}

/**
 * Set GA4 User ID based on account type
 * @param user - Current user object
 * @param businessAccount - Business account data if applicable
 */
export const setGA4UserId = (user: User | null, businessAccount: BusinessAccount | null = null) => {
  if (!user) return;

  try {
    if (businessAccount?.business_name || businessAccount?.name) {
      // B2B account - use business name formatted as lowercase with underscores
      const businessName = (businessAccount.business_name || businessAccount.name || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, ''); // Remove any special characters except underscores
      
      ReactGA.set({ user_id: businessName });
      console.log('GA4: Set user ID for business account:', businessName);
    } else if (user.protected?.email) {
      // Personal account - use email
      ReactGA.set({ user_id: user.protected.email });
      console.log('GA4: Set user ID for personal account:', user.protected.email);
    }
  } catch (error) {
    console.error('Error setting GA4 user ID:', error);
  }
};

/**
 * Track Skanteq events with custom parameters
 * @param eventName - Event name with skanteq_ prefix
 * @param eventData - Custom event data
 */
export const trackSkanteqEvent = (eventName: string, eventData: SkanteqEventData = {}) => {
  try {
    // Ensure event name has skanteq_ prefix
    const fullEventName = eventName.startsWith('skanteq_') ? eventName : `skanteq_${eventName}`;
    
    // Filter out undefined values
    const cleanedData = Object.entries(eventData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    ReactGA.event(fullEventName, cleanedData);
    console.log(`GA4: Tracked ${fullEventName}`, cleanedData);
  } catch (error) {
    console.error('Error tracking GA4 event:', error);
  }
};

/**
 * Track new account creation
 * @param user - User object
 * @param businessAccount - Business account if applicable
 * @param registrationMethod - How the account was created (e.g., 'email', 'google_sso', 'facebook_sso')
 */
export const trackAccountCreated = (
  user: User, 
  businessAccount: BusinessAccount | null = null,
  registrationMethod: string = 'email'
) => {
  const eventData: SkanteqEventData = {
    registration_method: registrationMethod,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('account_created', eventData);
};

/**
 * Track logged-in user login
 * @param user - User object
 * @param businessAccount - Business account if applicable
 */
export const trackUserLogin = (user: User, businessAccount: BusinessAccount | null = null) => {
  const eventData: SkanteqEventData = {};
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('user_login', eventData);
};

/**
 * Track experience creation
 * @param user - User object
 * @param experienceId - ID of created experience
 * @param experienceName - Name of created experience
 * @param businessAccount - Business account if applicable
 */
export const trackExperienceCreated = (
  user: User, 
  experienceId: string, 
  experienceName: string,
  businessAccount: BusinessAccount | null = null
) => {
  const eventData: SkanteqEventData = {
    experience_id: experienceId,
    experience_name: experienceName,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('experience_created', eventData);
};

/**
 * Track custom marker creation
 * @param user - User object
 * @param markerId - ID of created marker
 * @param markerName - Name of created marker
 * @param businessAccount - Business account if applicable
 */
export const trackMarkerCreated = (
  user: User,
  markerId: string,
  markerName: string,
  businessAccount: BusinessAccount | null = null
) => {
  const eventData: SkanteqEventData = {
    marker_id: markerId,
    marker_name: markerName,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('marker_created', eventData);
};

/**
 * Track add to cart
 * @param user - User object
 * @param productName - Product name
 * @param productPrice - Product price
 * @param productId - Product ID
 * @param experienceId - Related experience ID
 * @param markerId - Related marker ID
 * @param businessAccount - Business account if applicable
 */
export const trackAddToCart = (
  user: User,
  productName: string,
  productPrice: number,
  productId: string,
  experienceId?: string,
  markerId?: string,
  businessAccount: BusinessAccount | null = null
) => {
  const eventData: SkanteqEventData = {
    product_name: productName,
    product_price: productPrice,
    product_id: productId,
    experience_id: experienceId,
    marker_id: markerId,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('add_to_cart', eventData);
};

/**
 * Track purchase completion
 * @param user - User object
 * @param orderTotal - Total order value
 * @param products - Array of purchased products
 * @param businessAccount - Business account if applicable
 */
export const trackPurchase = (
  user: User,
  orderTotal: number,
  products: Array<{name: string, price: number, id: string, experience_id?: string, marker_id?: string}>,
  businessAccount: BusinessAccount | null = null
) => {
  const eventData: SkanteqEventData = {
    order_total: orderTotal,
    product_count: products.length,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  // Include product details
  products.forEach((product, index) => {
    eventData[`product_${index + 1}_name`] = product.name;
    eventData[`product_${index + 1}_price`] = product.price;
    eventData[`product_${index + 1}_id`] = product.id;
    if (product.experience_id) eventData[`product_${index + 1}_experience_id`] = product.experience_id;
    if (product.marker_id) eventData[`product_${index + 1}_marker_id`] = product.marker_id;
  });

  trackSkanteqEvent('purchase', eventData);
};

/**
 * Track experience publishing (subscription event)
 * @param user - User object
 * @param experienceId - ID of published experience
 * @param experienceName - Name of published experience
 * @param businessAccount - Business account if applicable
 */
export const trackExperiencePublished = (
  user: User,
  experienceId: string,
  experienceName: string,
  businessAccount: BusinessAccount | null = null
) => {
  const eventData: SkanteqEventData = {
    experience_id: experienceId,
    experience_name: experienceName,
  };
  
  if (businessAccount?.business_name || businessAccount?.name) {
    eventData.business_name = businessAccount.business_name || businessAccount.name;
  } else if (user.protected?.email) {
    eventData.user_email = user.protected.email;
  }

  trackSkanteqEvent('experience_published', eventData);
};

/**
 * Track QR scan
 * @param ownerEmail - Email of content owner
 * @param ownerBusinessName - Business name of content owner
 * @param markerId - Scanned marker ID
 * @param experienceId - Viewed experience ID
 */
export const trackQRScan = (
  ownerEmail: string | null,
  ownerBusinessName: string | null,
  markerId: string,
  experienceId: string
) => {
  const eventData: SkanteqEventData = {
    marker_id: markerId,
    experience_id: experienceId,
  };

  if (ownerBusinessName) {
    eventData.owner_business_name = ownerBusinessName;
  } else if (ownerEmail) {
    eventData.owner_email = ownerEmail;
  }

  trackSkanteqEvent('qr_scan', eventData);
};

/**
 * Track AR scan (marker tracking)
 * @param ownerEmail - Email of content owner
 * @param ownerBusinessName - Business name of content owner
 * @param markerId - Tracked marker ID
 * @param experienceId - Viewed experience ID
 */
export const trackARScan = (
  ownerEmail: string | null,
  ownerBusinessName: string | null,
  markerId: string,
  experienceId: string
) => {
  const eventData: SkanteqEventData = {
    marker_id: markerId,
    experience_id: experienceId,
  };

  if (ownerBusinessName) {
    eventData.owner_business_name = ownerBusinessName;
  } else if (ownerEmail) {
    eventData.owner_email = ownerEmail;
  }

  trackSkanteqEvent('ar_scan', eventData);
};

/**
 * Track link click
 * @param ownerEmail - Email of content owner
 * @param ownerBusinessName - Business name of content owner
 * @param markerId - Marker ID where click occurred
 * @param experienceId - Experience ID where click occurred
 * @param clickedUrl - URL that was clicked
 */
export const trackLinkClick = (
  ownerEmail: string | null,
  ownerBusinessName: string | null,
  markerId: string,
  experienceId: string,
  clickedUrl: string
) => {
  const eventData: SkanteqEventData = {
    marker_id: markerId,
    experience_id: experienceId,
    clicked_url: clickedUrl,
  };

  if (ownerBusinessName) {
    eventData.owner_business_name = ownerBusinessName;
  } else if (ownerEmail) {
    eventData.owner_email = ownerEmail;
  }

  trackSkanteqEvent('link_click', eventData);
};